import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import {
  Clock3,
  ExternalLink,
  FileText,
  MessageSquare,
  Flame,
  Heart,
  LayoutGrid,
  Search,
  Sparkles,
  Timer,
} from "lucide-react";
import { MAIN_URL, getApiHeaders } from "../../Configurations/Urls";
import { resolveOrganizationId } from "../../Utils/organizationSession";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import useStudentPortalFetch, {
  StudentPortalShell,
  StudentPortalState,
  httpUrl,
  ProgramCover,
  formatPortalMoney,
} from "./useStudentPortalFetch";

const CATALOG_TABS = [
  { id: "all", label: "All Courses", icon: LayoutGrid },
  { id: "recommended", label: "Recommended for Me", icon: Sparkles },
  { id: "popular", label: "Popular", icon: Flame },
  { id: "new", label: "New Courses", icon: Timer },
  { id: "wishlist", label: "Wishlist", icon: Heart },
];

const COVER_TONES = [
  ["#0e7490", "#0891b2"],
  ["#4338ca", "#6366f1"],
  ["#0f766e", "#14b8a6"],
  ["#6d28d9", "#8b5cf6"],
  ["#075985", "#0284c7"],
  ["#9f1239", "#e11d48"],
  ["#b45309", "#f59e0b"],
];

function resolveCatalogFee(row) {
  const structures = Array.isArray(row?.fee_structures)
    ? row.fee_structures
    : Array.isArray(row?.feeStructures)
      ? row.feeStructures
      : [];
  const preferred = String(
    row?.display_currency || row?.fee_currency || ""
  )
    .trim()
    .toUpperCase();

  if (row?.display_fee != null && row.display_fee !== "") {
    return {
      amount: row.display_fee,
      currency: preferred || "INR",
    };
  }

  const byCode = (code) =>
    structures.find(
      (f) => String(f?.currency_code || "").toUpperCase() === code
    );

  const match =
    (preferred && preferred !== "INR" ? byCode(preferred) : null) ||
    byCode("INR") ||
    structures.find((f) => f?.amount != null && f.amount !== "");

  if (match) {
    return {
      amount: match.amount,
      currency: String(match.currency_code || preferred || "INR").toUpperCase(),
    };
  }

  if (row?.fee_in_inr != null && row.fee_in_inr !== "") {
    return { amount: row.fee_in_inr, currency: "INR" };
  }
  if (row?.fees_in_inr != null && row.fees_in_inr !== "") {
    return { amount: row.fees_in_inr, currency: "INR" };
  }

  return { amount: null, currency: "INR" };
}

function resolveCategoryName(row) {
  return (
    row?.category_name ||
    row?.category?.training_program_category_name ||
    row?.category?.category_name ||
    "Uncategorized"
  );
}

function BuyNowControl({ url, className = "ems-catalog-buy-now", children = "Buy Now" }) {
  const href = httpUrl(url);
  const stop = (e) => e.stopPropagation();
  if (href) {
    return (
      <a
        className={className}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={stop}
      >
        {children}
      </a>
    );
  }
  return (
    <button type="button" className={`${className} is-static`} onClick={stop}>
      {children}
    </button>
  );
}

function initials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "C";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function moduleMeta(module) {
  return [
    module.duration_in_hours != null && module.duration_in_hours !== ""
      ? `${module.duration_in_hours} hrs`
      : null,
    module.duration_in_days != null && module.duration_in_days !== ""
      ? `${module.duration_in_days} days`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

function programModules(row) {
  const raw =
    row?.modules ??
    row?.training_program_modules ??
    row?.trainingProgramModules ??
    row?.module_names ??
    [];
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object") {
    if (Array.isArray(raw.data)) return raw.data;
    return Object.values(raw).filter(
      (item) =>
        item &&
        typeof item === "object" &&
        (item.module_name || item.organization_ems_training_program_module_id)
    );
  }
  return [];
}

function coverTone(seed) {
  const s = String(seed || "");
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) {
    hash = (hash + s.charCodeAt(i) * (i + 1)) % COVER_TONES.length;
  }
  return COVER_TONES[hash];
}

function emptyMessage(tab, search) {
  if (search) return "No courses match your search.";
  if (tab === "recommended") {
    return "No recommendations yet. Enroll in a course to see similar programs.";
  }
  if (tab === "popular") return "No popular courses yet.";
  if (tab === "new") return "No new courses yet.";
  if (tab === "wishlist") return "No saved courses yet. Tap the heart to add one.";
  return "Nothing to show yet.";
}

function buildCategories(data) {
  if (Array.isArray(data?.categories) && data.categories.length > 0) {
    return data.categories.map((group) => ({
      category_name: group.category_name || "Uncategorized",
      programs: Array.isArray(group.programs) ? group.programs : [],
    }));
  }

  const flat = Array.isArray(data?.programs)
    ? data.programs
    : Array.isArray(data)
      ? data
      : [];

  const map = new Map();
  flat.forEach((row) => {
    const name = resolveCategoryName(row);
    if (!map.has(name)) map.set(name, []);
    map.get(name).push(row);
  });

  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([category_name, programs]) => ({ category_name, programs }));
}

export default function StudentCatalogPage() {
  const { userData } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = String(searchParams.get("search") || "").trim();
  const programParam = String(
    searchParams.get("program") || searchParams.get("program_id") || ""
  ).trim();
  const [searchInput, setSearchInput] = useState(initialSearch);
  const [search, setSearch] = useState(initialSearch);
  const [tab, setTab] = useState("all");
  const [wishlistOverride, setWishlistOverride] = useState({});
  const [wishlistBusyId, setWishlistBusyId] = useState(null);
  const [wishlistError, setWishlistError] = useState("");
  const [detailProgram, setDetailProgram] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setWishlistOverride({});
    setWishlistError("");
  }, [tab, search]);

  const { data, loading, error } = useStudentPortalFetch("catalog", {
    filter: tab,
    ...(search ? { search } : {}),
  });
  const rawCategories = buildCategories(data);
  const categories =
    tab === "wishlist"
      ? rawCategories
          .map((group) => ({
            ...group,
            programs: group.programs.filter((row) => {
              const id = Number(row.organization_ems_training_program_id);
              if (Object.prototype.hasOwnProperty.call(wishlistOverride, id)) {
                return wishlistOverride[id];
              }
              return Boolean(row.in_wishlist);
            }),
          }))
          .filter((group) => group.programs.length > 0)
      : rawCategories;
  const programCount = categories.reduce(
    (sum, group) => sum + group.programs.length,
    0
  );
  const showCategoryTitles =
    data?.group_by_category !== false && (tab === "all" || tab === "recommended");

  const allPrograms = categories.flatMap((group) => group.programs);

  useEffect(() => {
    if (!programParam) {
      setDetailProgram(null);
      return;
    }
    const found = allPrograms.find(
      (row) =>
        String(row?.organization_ems_training_program_id) === String(programParam)
    );
    if (found) setDetailProgram(found);
  }, [programParam, data, tab, search]);

  const openProgramDetail = (row) => {
    if (!row) return;
    const id = row.organization_ems_training_program_id;
    setDetailProgram(row);
    if (id == null || String(id).trim() === "") return;
    const next = new URLSearchParams(searchParams);
    next.set("program", String(id));
    setSearchParams(next, { replace: true });
  };

  const closeProgramDetail = () => {
    setDetailProgram(null);
    if (!programParam) return;
    const next = new URLSearchParams(searchParams);
    next.delete("program");
    next.delete("program_id");
    setSearchParams(next, { replace: true });
  };

  const isInWishlist = (row) => {
    const id = Number(row.organization_ems_training_program_id);
    if (Object.prototype.hasOwnProperty.call(wishlistOverride, id)) {
      return wishlistOverride[id];
    }
    return Boolean(row.in_wishlist);
  };

  const toggleWishlist = async (row) => {
    const id = Number(row.organization_ems_training_program_id);
    if (!id || wishlistBusyId) return;

    const next = !isInWishlist(row);
    setWishlistError("");
    setWishlistBusyId(id);
    setWishlistOverride((prev) => ({ ...prev, [id]: next }));

    try {
      const orgId = resolveOrganizationId(userData);
      const url = `${MAIN_URL}/api/ems/portal/catalog/wishlist/${id}`;
      const config = {
        headers: getApiHeaders(),
        params: orgId ? { organization_id: orgId } : undefined,
      };
      if (next) {
        await axios.post(url, {}, config);
      } else {
        await axios.delete(url, config);
      }
    } catch (err) {
      setWishlistOverride((prev) => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
      setWishlistError(
        err?.response?.data?.message ||
          err?.message ||
          "Could not update wishlist."
      );
    } finally {
      setWishlistBusyId(null);
    }
  };

  return (
    <StudentPortalShell
      className="ems-catalog-advanced"
      title="Available Courses"
      subtitle="Discover programs, save favorites, and pick what to learn next."
      headerRight={
        <label className="ems-student-search-wrap ems-catalog-search">
          <Search size={16} strokeWidth={2} aria-hidden="true" />
          <input
            type="search"
            className="ems-student-search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name, code, or category"
            aria-label="Search available courses"
          />
        </label>
      }
    >
      <div className="ems-catalog-toolbar">
        <div className="ems-catalog-tabs" role="tablist" aria-label="Course filters">
          {CATALOG_TABS.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={tab === item.id}
                className={`ems-catalog-tab${tab === item.id ? " is-active" : ""}`}
                onClick={() => setTab(item.id)}
              >
                <Icon size={15} strokeWidth={2.2} />
                {item.label}
              </button>
            );
          })}
        </div>
        {!loading && !error ? (
          <p className="ems-catalog-count">
            {programCount} {programCount === 1 ? "program" : "programs"}
          </p>
        ) : null}
      </div>
      {wishlistError ? (
        <div className="ems-student-portal-state ems-student-portal-error">
          {wishlistError}
        </div>
      ) : null}
      <StudentPortalState
        loading={loading}
        error={error}
        empty={!loading && !error && programCount === 0}
        emptyText={emptyMessage(tab, search)}
      >
        <div className="ems-student-catalog">
          {categories.map((group) => {
            const [from, to] = coverTone(group.category_name);
            return (
              <section className="ems-student-section" key={group.category_name}>
                {showCategoryTitles ? (
                  <div className="ems-catalog-section-head">
                    <h3 className="ems-student-section-title">
                      {group.category_name}
                    </h3>
                    <span>
                      {group.programs.length}{" "}
                      {group.programs.length === 1 ? "course" : "courses"}
                    </span>
                  </div>
                ) : null}
                <div className="ems-student-cards ems-course-cards ems-catalog-grid">
                  {group.programs.map((row) => {
                    const name =
                      row.training_program_name || "Training program";
                    const saved = isInWishlist(row);
                    const programId = Number(
                      row.organization_ems_training_program_id
                    );
                    const feeInfo = resolveCatalogFee(row);
                    const fee =
                      feeInfo.amount == null || feeInfo.amount === ""
                        ? "—"
                        : formatPortalMoney(feeInfo.amount, feeInfo.currency);
                    const [cardFrom, toColor] = coverTone(
                      name + (row.training_program_code || "")
                    );
                    const photoUrl = httpUrl(row.photo_url);
                    const notesUrl = httpUrl(row.notes_url);
                    const interviewQaUrl = httpUrl(row.interview_qa_url);
                    const websiteUrl = httpUrl(row.website_url);
                    const category = resolveCategoryName(row);
                    const modules = programModules(row);

                    return (
                      <article
                        key={programId}
                        className="ems-student-card ems-catalog-card"
                        role="button"
                        tabIndex={0}
                        onClick={() => openProgramDetail(row)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            openProgramDetail(row);
                          }
                        }}
                      >
                        <ProgramCover
                          src={photoUrl}
                          alt={name}
                          className="ems-catalog-card-cover"
                          style={{
                            background: `linear-gradient(135deg, ${from || cardFrom} 0%, ${to || toColor} 100%)`,
                          }}
                          overlay={
                            <>
                              <span className="ems-catalog-card-code">
                                {row.training_program_code || "Program"}
                              </span>
                              <button
                                type="button"
                                className={`ems-course-wish${saved ? " is-saved" : ""}`}
                                disabled={wishlistBusyId === programId}
                                aria-pressed={saved}
                                aria-label={
                                  saved
                                    ? "Remove from wishlist"
                                    : "Add to wishlist"
                                }
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleWishlist(row);
                                }}
                              >
                                <Heart
                                  size={16}
                                  strokeWidth={2}
                                  fill={saved ? "currentColor" : "none"}
                                />
                              </button>
                            </>
                          }
                        >
                          <span className="ems-catalog-card-mark">
                            {initials(name)}
                          </span>
                        </ProgramCover>
                        <div className="ems-catalog-card-body">
                          <div className="ems-catalog-card-title-row">
                            <h3>{name}</h3>
                            {row.already_purchased ? (
                              <span className="ems-student-badge active">
                                Enrolled
                              </span>
                            ) : (
                              <span className="ems-student-badge ems-student-badge-neutral">
                                Open
                              </span>
                            )}
                          </div>
                          <p className="ems-catalog-card-category">{category}</p>
                          {row.offer ? (
                            <div className="ems-catalog-offer">
                              <b>{row.offer.discount_label || "Offer"}</b>
                              <span>{row.offer.offer_name}</span>
                              <small>{row.offer.period_label || "Limited time"}</small>
                            </div>
                          ) : null}
                          <div className="ems-catalog-card-stats">
                            <span>
                              <Clock3 size={14} />
                              {row.duration_in_hours != null
                                ? `${row.duration_in_hours} hrs`
                                : "—"}
                            </span>
                            <span>
                              <Timer size={14} />
                              {row.duration_in_months != null
                                ? `${row.duration_in_months} mo`
                                : "—"}
                            </span>
                            <span className="ems-catalog-notes">
                              <FileText size={14} />
                              Modules ({modules.length})
                            </span>
                            {notesUrl ? (
                              <a
                                className="ems-catalog-notes"
                                href={notesUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <FileText size={14} />
                                Notes
                              </a>
                            ) : null}
                            {interviewQaUrl ? (
                              <a
                                className="ems-catalog-notes"
                                href={interviewQaUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MessageSquare size={14} />
                                Interview Q/A
                              </a>
                            ) : null}
                          </div>
                          <div className="ems-catalog-card-footer">
                            <div>
                              <span>Fee</span>
                              <b>{fee}</b>
                            </div>
                            {row.already_purchased ? (
                              <em>Already enrolled</em>
                            ) : (
                              <BuyNowControl url={websiteUrl}>
                                Buy Now
                                <ExternalLink size={13} />
                              </BuyNowControl>
                            )}
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      </StudentPortalState>

      {detailProgram ? (
        <div
          className="ems-student-modal-backdrop ems-catalog-detail-backdrop"
          role="presentation"
          onClick={closeProgramDetail}
        >
          <div
            className="ems-student-modal ems-catalog-detail-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ems-catalog-detail-title"
            onClick={(e) => e.stopPropagation()}
          >
            {(() => {
              const name =
                detailProgram.training_program_name ||
                detailProgram.program_name ||
                "Training program";
              const [from, to] = coverTone(
                name + (detailProgram.training_program_code || "")
              );
              const photoUrl = httpUrl(detailProgram.photo_url);
              const notesUrl = httpUrl(detailProgram.notes_url);
              const interviewQaUrl = httpUrl(detailProgram.interview_qa_url);
              const websiteUrl = httpUrl(detailProgram.website_url);
              const feeInfo = resolveCatalogFee(detailProgram);
              const fee =
                feeInfo.amount == null || feeInfo.amount === ""
                  ? "—"
                  : formatPortalMoney(feeInfo.amount, feeInfo.currency);
              const modules = programModules(detailProgram);
              const description = String(
                detailProgram.description || ""
              ).trim();

              return (
                <>
                  <div className="ems-student-modal-header">
                    <h3 id="ems-catalog-detail-title">{name}</h3>
                    <button
                      type="button"
                      className="ems-student-modal-close"
                      onClick={closeProgramDetail}
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </div>
                  <div className="ems-student-modal-body ems-catalog-detail-body">
                    <ProgramCover
                      src={photoUrl}
                      alt={name}
                      className="ems-catalog-detail-cover"
                      style={{
                        background: `linear-gradient(135deg, ${from}, ${to})`,
                      }}
                    >
                      <span className="ems-catalog-card-mark">
                        {initials(name)}
                      </span>
                    </ProgramCover>
                    <p className="ems-catalog-detail-code">
                      {detailProgram.training_program_code || "Program"}
                      {" · "}
                      {resolveCategoryName(detailProgram)}
                    </p>
                    {detailProgram.already_purchased ? (
                      <span className="ems-student-badge active">Enrolled</span>
                    ) : (
                      <span className="ems-student-badge ems-student-badge-neutral">
                        Open
                      </span>
                    )}
                    {description ? (
                      <p className="ems-catalog-detail-desc">{description}</p>
                    ) : null}
                    {detailProgram.offer ? (
                      <div className="ems-catalog-offer">
                        <b>{detailProgram.offer.discount_label || "Offer"}</b>
                        <span>{detailProgram.offer.offer_name}</span>
                        <small>{detailProgram.offer.period_label || "Limited time"}</small>
                      </div>
                    ) : null}
                    <dl className="ems-catalog-detail-facts">
                      <div>
                        <dt>Duration (hours)</dt>
                        <dd>
                          {detailProgram.duration_in_hours != null
                            ? `${detailProgram.duration_in_hours} hrs`
                            : "—"}
                        </dd>
                      </div>
                      <div>
                        <dt>Duration (months)</dt>
                        <dd>
                          {detailProgram.duration_in_months != null
                            ? `${detailProgram.duration_in_months} months`
                            : "—"}
                        </dd>
                      </div>
                      <div>
                        <dt>
                          Fee
                          {feeInfo.currency ? ` (${feeInfo.currency})` : ""}
                        </dt>
                        <dd>{fee}</dd>
                      </div>
                    </dl>
                    <section
                      className="ems-catalog-detail-modules"
                      aria-label="Program modules"
                    >
                      <div className="ems-catalog-detail-modules-head">
                        <strong>Modules</strong>
                        <span>{modules.length}</span>
                      </div>
                      {modules.length === 0 ? (
                        <p className="ems-student-meta">
                          No modules configured for this program.
                        </p>
                      ) : (
                        <ol className="ems-catalog-detail-modules-list">
                          {modules.map((module, idx) => (
                            <li
                              key={
                                module.organization_ems_training_program_module_id ||
                                `${module.module_name || "module"}-${idx}`
                              }
                            >
                              <strong>
                                {module.module_name || `Module ${idx + 1}`}
                              </strong>
                              {moduleMeta(module) ? (
                                <span>{moduleMeta(module)}</span>
                              ) : null}
                            </li>
                          ))}
                        </ol>
                      )}
                    </section>
                    <div className="ems-catalog-detail-links">
                      {notesUrl ? (
                        <a
                          className="ems-student-link"
                          href={notesUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open notes
                        </a>
                      ) : null}
                      {interviewQaUrl ? (
                        <a
                          className="ems-student-link"
                          href={interviewQaUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open Interview Q/A
                        </a>
                      ) : null}
                    </div>
                  </div>
                  <div className="ems-student-modal-footer">
                    <button
                      type="button"
                      className="ems-student-btn ems-student-btn-secondary ems-student-btn-sm"
                      onClick={closeProgramDetail}
                    >
                      Close
                    </button>
                    {!detailProgram.already_purchased ? (
                      <BuyNowControl
                        url={websiteUrl}
                        className="ems-student-btn ems-student-btn-primary ems-student-btn-sm"
                      />
                    ) : null}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      ) : null}

    </StudentPortalShell>
  );
}
